import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
 /charts — THE CHARTS · Monthly community-crowned tracks

 Public feed. Anyone can browse and listen. Signed-in users
 can upvote / downvote. One vote per user per track. Judge
 DeGagné anti-cheat tiebreaker on the monthly finals (post-launch).

 Writer-first rule enforced at submission — no one-liner spam.
 ============================================================ */

const STYLE_LABELS = {
  cinematic_pop: "CINEMATIC POP",
  inspirational_ballad: "BALLAD",
  singer_songwriter: "SINGER-SONGWRITER",
  indie_anthem: "INDIE ANTHEM",
  worship_gospel: "GOSPEL",
  reggae: "REGGAE",
  ska_punk_reggae: "SKA PUNK",
  sunny_alt: "SUNNY ALT",
  happy_hip_hop: "HAPPY HIP-HOP",
  hick_hop: "HICK-HOP",
  aggressive: "AGGRESSIVE",
  boom_bap: "BOOM BAP",
  trap: "TRAP",
  smooth: "SMOOTH",
  conscious: "CONSCIOUS",
  hip_hop: "HIP HOP",
  drill: "DRILL",
  country: "COUNTRY",
  pop: "POP",
  rnb: "R&B",
  edm: "EDM",
  latin: "LATIN",
  rock: "ROCK",
  dark_viking: "DARK VIKING",
};

const STYLE_COLORS = {
 cinematic_pop: "text-yt-judge",
 inspirational_ballad: "text-white",
 singer_songwriter: "text-yt-yellow",
 indie_anthem: "text-yt-judge",
 worship_gospel: "text-yt-yellow",
 reggae: "text-yt-yellow",
 ska_punk_reggae: "text-yt-red",
 sunny_alt: "text-yt-judge",
 happy_hip_hop: "text-yt-yellow",
 hick_hop: "text-yt-yellow",
 aggressive: "text-yt-red",
 boom_bap: "text-yt-yellow",
 trap: "text-yt-judge",
 smooth: "text-white",
 conscious: "text-yt-yellow",
 hip_hop: "text-yt-yellow",
 drill: "text-yt-red",
 country: "text-yt-yellow",
 pop: "text-yt-judge",
 rnb: "text-white",
 edm: "text-yt-judge",
 latin: "text-yt-red",
 rock: "text-white",
 dark_viking: "text-yt-red",
};

export default function Charts() {
 const { user } = useAuth();
 const [tracks, setTracks] = useState([]);
 const [myVotes, setMyVotes] = useState({});
 const [sort, setSort] = useState("recent");
 const [style, setStyle] = useState("");
 const [cleanOnly, setCleanOnly] = useState(false);
 const [loading, setLoading] = useState(true);

 // ── Play All queue ─────────────────────────────────────────────
 // Paul asked for a "playthrough" so the charts play song-to-song
 // without clicking each one. State lives on the parent; the audio
 // elements themselves live in TrackRow and are reached via refs.
 const audioRefs = useRef({});           // { [trackId]: HTMLAudioElement }
 const [queueOn, setQueueOn] = useState(false);
 const [playingId, setPlayingId] = useState(null);

 const registerAudio = (id, el) => {
 if (el) audioRefs.current[id] = el;
 else delete audioRefs.current[id];
 };

 const playTrackAt = (idx) => {
 const t = tracks[idx];
 if (!t) { setQueueOn(false); setPlayingId(null); return; }
 if (!t.audio_url) { playTrackAt(idx + 1); return; }
 const el = audioRefs.current[t.id];
 if (!el) { playTrackAt(idx + 1); return; }
 setPlayingId(t.id);
 // scroll it into view so Paul can see what's playing
 try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
 el.play().catch(() => {
 // autoplay blocked — try next so queue doesn't stall silently
 playTrackAt(idx + 1);
 });
 };

 const handleEnded = (trackId) => {
 if (!queueOn) return;
 const idx = tracks.findIndex((t) => t.id === trackId);
 if (idx < 0) return;
 playTrackAt(idx + 1);
 };

 const startPlayAll = () => {
 setQueueOn(true);
 // start from first track with audio
 playTrackAt(0);
 };

 const stopPlayAll = () => {
 setQueueOn(false);
 setPlayingId(null);
 Object.values(audioRefs.current).forEach((el) => { try { el.pause(); } catch {} });
 };

 const load = async () => {
 setLoading(true);
 try {
 const params = { sort, limit: 50 };
 if (style) params.style = style;
 if (cleanOnly) params.include_explicit = false;
 const { data } = await axios.get(`${API}/charts`, { params });
 setTracks(data || []);
 if (user) {
 const votes = await axios.get(`${API}/tracks/my-votes`);
 setMyVotes(votes.data || {});
 }
 } catch (e) {
 setTracks([]);
 }
 setLoading(false);
 };

 useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, style, cleanOnly, user]);

 const vote = async (trackId, value) => {
 if (!user) return;
 const current = myVotes[trackId] || 0;
 const next = current === value ? 0 : value; // toggle off if same
 try {
 const { data } = await axios.post(`${API}/tracks/${trackId}/vote`, { value: next });
 setMyVotes(v => ({ ...v, [trackId]: next }));
 setTracks(ts => ts.map(t => t.id === trackId
 ? { ...t, upvotes: data.upvotes, downvotes: data.downvotes, score: data.score }
 : t));
 } catch (e) {
 // silent — likely 400 (can't vote own track)
 }
 };

 return (
 <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
 {/* Header */}
 <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
 <div>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> THE CHARTS · MONTHLY CROWN</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9]">
 <span className="text-white">LYRICAL</span>{" "}
 <span className="text-yt-red">CHARTS</span>
 </h1>
 <p className="font-mono text-[10px] md:text-xs tracking-widest text-yt-muted mt-3 max-w-xl">
 WRITER-FIRST · YOUR LYRICS · SUNO VOCALS · COMMUNITY-VOTED · NO BOTS · NO AUTO-GEN
 </p>
 </div>
 <div className="flex gap-2 flex-wrap">
 <Link
   to="/crown"
   data-testid="charts-crown-link"
   className="border-2 border-white text-white font-display text-sm md:text-base tracking-widest px-4 py-3 hover:bg-white hover:text-black transition-colors"
 >
   MONTHLY CROWN
 </Link>
 {user && (
 <Link
 to="/my-tracks"
 data-testid="charts-my-tracks"
 className="border-2 border-yt-yellow text-yt-yellow font-display text-sm md:text-base tracking-widest px-4 py-3 hover:bg-yt-yellow hover:text-black transition-colors"
 >
 MY TRACKS
 </Link>
 )}
 <Link to="/charts/submit"
 data-testid="charts-submit-cta"
 className="bg-yt-yellow text-black font-display text-lg tracking-widest px-5 py-3 shadow-brutal hover:bg-white transition-colors"
 >
 ▶ DROP YOUR TRACK
 </Link>
 </div>
 </div>

 {/* Filters */}
 <div className="flex flex-wrap gap-2 mb-6 items-center">
 <button
 data-testid={queueOn ? "charts-stop-all" : "charts-play-all"}
 onClick={queueOn ? stopPlayAll : startPlayAll}
 className={`font-display text-sm tracking-widest px-4 py-2 border-2 transition-colors ${
 queueOn
 ? "border-yt-red bg-yt-red text-white hover:bg-black hover:text-yt-red"
 : "border-yt-yellow bg-yt-yellow text-black shadow-brutal hover:bg-white"
 }`}
 title={queueOn ? "Stop the playthrough" : "Play every track top-to-bottom"}
 >
 {queueOn ? "■ STOP PLAYTHROUGH" : "▶ PLAY ALL"}
 </button>
 <div className="w-2" />
 <FilterButton active={sort === "score"} onClick={() => setSort("score")} testId="sort-score">TOP</FilterButton>
 <FilterButton active={sort === "recent"} onClick={() => setSort("recent")} testId="sort-recent">RECENT</FilterButton>
 <div className="w-4" />
 <FilterButton active={cleanOnly} onClick={() => setCleanOnly(v => !v)} testId="filter-clean-only">
   {cleanOnly ? "✓ CLEAN ONLY" : "CLEAN ONLY"}
 </FilterButton>
 <div className="w-4" />
 <FilterButton active={style === ""} onClick={() => setStyle("")} testId="style-all">ALL STYLES</FilterButton>
 {Object.entries(STYLE_LABELS).map(([k, label]) => (
 <FilterButton key={k} active={style === k} onClick={() => setStyle(k)} testId={`style-${k}`}>
 {label}
 </FilterButton>
 ))}
 </div>

 {/* Genre sub-chart banner — makes single-genre view feel like its
     own leaderboard instead of just a filter. */}
 {style && (
   <div data-testid="genre-banner" className="border-2 border-yt-yellow bg-yt-surface p-4 mb-6 flex items-center justify-between gap-4 flex-wrap">
     <div>
       <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-1">GENRE SUB-CHART</div>
       <div className={`font-display text-3xl md:text-4xl tracking-tight ${STYLE_COLORS[style] || "text-white"}`}>
         {STYLE_LABELS[style] || style.toUpperCase()} · TOP OF THE MONTH
       </div>
     </div>
     <button
       data-testid="genre-clear"
       onClick={() => setStyle("")}
       className="font-mono text-[10px] tracking-widest text-yt-muted border border-yt-border px-3 py-2 hover:border-yt-yellow hover:text-yt-yellow"
     >
       ← BACK TO ALL
     </button>
   </div>
 )}

 {/* Feed */}
 {loading && <div className="font-mono text-yt-muted animate-pulse-red">LOADING TRACKS...</div>}
 {!loading && tracks.length === 0 && (
 <div className="border-2 border-yt-border bg-yt-surface p-10 text-center">
 <div className="font-display text-3xl md:text-5xl mb-3">CHARTS ARE EMPTY</div>
 <p className="font-mono text-sm text-yt-muted mb-6">Be the first artist on the wall. Your lyrics. Your bars. Your crown.</p>
 <Link to="/charts/submit" data-testid="charts-empty-submit"
 className="inline-block bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 shadow-brutal">
 ▶ DROP THE FIRST TRACK
 </Link>
 </div>
 )}
 {!loading && tracks.length > 0 && (
 <div className="space-y-3">
 {tracks.map((t, idx) => (
 <TrackRow
 key={t.id}
 rank={idx + 1}
 track={t}
 myVote={myVotes[t.id] || 0}
 canVote={!!user}
 onVote={vote}
 registerAudio={registerAudio}
 onEnded={handleEnded}
 isCurrent={playingId === t.id}
 />
 ))}
 </div>
 )}
 </div>
 );
}

function FilterButton({ active, onClick, children, testId }) {
 return (
 <button
 data-testid={testId}
 onClick={onClick}
 className={`font-mono text-[10px] tracking-widest px-3 py-2 border-2 transition-colors ${
 active
 ? "border-yt-yellow text-yt-yellow bg-yt-yellow/10"
 : "border-yt-border text-yt-muted hover:border-yt-yellow hover:text-yt-yellow"
 }`}
 >
 {children}
 </button>
 );
}

function TrackRow({ rank, track, myVote, canVote, onVote, registerAudio, onEnded, isCurrent }) {
 const upActive = myVote === 1;
 const downActive = myVote === -1;
 return (
 <div
 data-testid={`chart-track-${track.id}`}
 className={`bg-yt-surface border-2 ${isCurrent ? "border-yt-yellow shadow-brutal" : "border-yt-border hover:border-yt-yellow"} transition-colors p-4 md:p-5 flex items-center gap-4`}
 >
 {/* Rank */}
 <div className="font-display text-3xl md:text-4xl text-yt-yellow w-10 md:w-14 text-center">
 {rank}
 </div>

 {track.image_url && (
 <img src={track.image_url} alt={`${track.title} cover`} data-testid={`chart-cover-${track.id}`}
 className="w-16 h-16 md:w-20 md:h-20 object-cover border-2 border-yt-border shrink-0" />
 )}
 {/* Play + info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-3 flex-wrap mb-2">
 <div className={`font-mono text-[9px] tracking-widest ${STYLE_COLORS[track.style] || "text-white"}`}>
 {STYLE_LABELS[track.style] || track.style?.toUpperCase()}
 </div>
 <div className="font-mono text-[9px] tracking-widest text-yt-muted">
 {track.anonymous ? "◇ ANON" : `@${track.publish_as}`}
 </div>
 {track.explicit && (
   <div data-testid={`explicit-badge-${track.id}`}
        className="font-mono text-[9px] tracking-widest text-black bg-yt-red px-1.5 py-0.5 border border-yt-red">
     E
   </div>
 )}
 </div>
 <Link to={`/tracks/${track.id}`} data-testid={`chart-track-link-${track.id}`}
 className="font-display text-lg md:text-2xl truncate block hover:text-yt-yellow transition-colors">
 {track.title}
 </Link>
 {track.audio_url && (
 <audio
 ref={(el) => registerAudio && registerAudio(track.id, el)}
 data-testid={`chart-audio-${track.id}`}
 src={track.audio_url}
 controls
 preload="none"
 onEnded={() => onEnded && onEnded(track.id)}
 className="w-full mt-3"
 />
 )}
 <Link to={`/tracks/${track.id}`}
 className="inline-block mt-2 font-mono text-[10px] tracking-widest text-yt-yellow hover:text-white">
 VIEW · COMMENT · SHARE ▶
 </Link>
 </div>

 {/* Vote column */}
 <div className="flex flex-col items-center gap-1">
 <button
 data-testid={`upvote-${track.id}`}
 disabled={!canVote}
 onClick={() => onVote(track.id, 1)}
 title={canVote ? "Upvote" : "Sign in to vote"}
 className={`w-9 h-9 border-2 flex items-center justify-center font-bold transition-colors ${
 upActive
 ? "border-yt-judge bg-yt-judge text-black"
 : "border-yt-border text-yt-muted hover:border-yt-judge hover:text-yt-judge"
 } ${!canVote && "opacity-40 cursor-not-allowed"}`}
 >
 ▲
 </button>
 <div className={`font-display text-lg ${track.score > 0 ? "text-yt-judge" : track.score < 0 ? "text-yt-red" : "text-white"}`}
 data-testid={`score-${track.id}`}>
 {track.score}
 </div>
 <button
 data-testid={`downvote-${track.id}`}
 disabled={!canVote}
 onClick={() => onVote(track.id, -1)}
 title={canVote ? "Downvote" : "Sign in to vote"}
 className={`w-9 h-9 border-2 flex items-center justify-center font-bold transition-colors ${
 downActive
 ? "border-yt-red bg-yt-red text-white"
 : "border-yt-border text-yt-muted hover:border-yt-red hover:text-yt-red"
 } ${!canVote && "opacity-40 cursor-not-allowed"}`}
 >
 ▼
 </button>
 </div>
 </div>
 );
}
